import React, { Component, Fragment, useEffect, useRef } from 'react';
import { Animated, Easing, StyleSheet, Text, View } from 'react-native';
import OverviewScreen from '../../../helperComponents/OverviewScreen';
import { useSignedInContext } from '../../../../context/SignedInContext';
import { GradientButton } from '../../../helperComponents/gradients/GradientButton';
import { UIActivityIndicator } from '@mightybyte/rnw.components.activity-indicators';
import HighlightCard from '../../../helperComponents/HighlightCard';
import { mbGetMediumImage } from '@mightybyte/rnw.components.image';
import { FONT_STYLES, textStyles } from '../../../../constants/textStyles';
import { mbTextStyles } from '@mightybyte/rnw.utils.style-utils';
import { ImageUrls } from '@sapphicsavvy/business';
import FastImage from 'react-native-fast-image';
import { usePrefetchImages } from '../../../../hooks/usePrefetchImages';

class AvatarController extends Component<{
    animatedValue: number;
    avatars: ImageUrls[];
}> {
    render(): React.ReactNode {
        const index = Math.floor(this.props.animatedValue);
        return (
            <FastImage
                style={styles.image}
                source={mbGetMediumImage(this.props.avatars[index], {
                    asUri: true,
                })}
            />
        );
    }
}

const AnimatedAvatarController =
    Animated.createAnimatedComponent(AvatarController);

type ResultsProps = {
    totalUsers: number;
    avatars: ImageUrls[];
    quarterNumber?: number;
    isLoading?: boolean;
    onPress?: () => void;
};

export default function Results({
    totalUsers,
    quarterNumber,
    avatars,
    isLoading,
    onPress,
}: ResultsProps) {
    const { currentUserData } = useSignedInContext();
    const animatedValue = useRef(new Animated.Value(0)).current;

    usePrefetchImages({
        urls: avatars.map((avatar) => mbGetMediumImage(avatar)),
        enabled: avatars.length > 0,
    });

    useEffect(() => {
        if (avatars.length > 0) {
            const animation = Animated.loop(
                Animated.timing(animatedValue, {
                    toValue: avatars.length,
                    duration: avatars.length * 300,
                    useNativeDriver: false,
                    easing: Easing.linear,
                }),
            );
            animation.start();
            return () => animation.stop();
        }
    }, [animatedValue, avatars.length]);

    return (
        <OverviewScreen
            topSectionStyle={styles.topSection}
            bottomSectionStyle={styles.bottomSection}
            gradientColors={['#EF3A24', '#F9943B']}
            topSectionChildren={
                <Fragment>
                    <Text style={styles.topTitleText}>
                        QUARTER {quarterNumber}
                    </Text>
                    <Text style={styles.titleText}>RESULTS</Text>
                    <View style={styles.imageContainer}>
                        <FastImage
                            style={[styles.image, styles.staticImage]}
                            source={mbGetMediumImage(
                                currentUserData?.avatarUrl,
                                { asUri: true },
                            )}
                        />
                        <AnimatedAvatarController
                            animatedValue={animatedValue}
                            avatars={avatars}
                        />
                    </View>
                </Fragment>
            }
            overlay
        >
            {isLoading ? (
                <View>
                    <UIActivityIndicator size={30} color="#EF3A24" />
                </View>
            ) : (
                <View style={styles.content}>
                    <Text style={styles.greetingText}>
                        Hey @{currentUserData?.userName}! This week's correct
                        answers are based on answers from all
                    </Text>
                    <HighlightCard
                        text={totalUsers}
                        style={styles.highlightCard}
                        textStyle={styles.highlightCardText}
                    />
                    <Text style={styles.subGreetingText}>
                        sapphics who played this quarter (including YOU)
                    </Text>
                    <GradientButton
                        title="Next"
                        containerStyle={styles.btn}
                        onPress={onPress}
                    />
                </View>
            )}
        </OverviewScreen>
    );
}

const styles = StyleSheet.create({
    bottomSection: {
        height: '40%',
    },
    topSection: {
        height: '60%',
        alignItems: 'center',
        justifyContent: 'center',
    },
    imageContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 4,
        borderRadius: 160,
        borderColor: '#FFFFFF',
        marginTop: 30,
    },
    image: {
        width: 181,
        height: 181,
        borderRadius: 160,
    },
    staticImage: {
        position: 'absolute',
    },
    topTitleText: mbTextStyles([
        textStyles.largestText,
        {
            fontSize: 25,
            fontFamily: FONT_STYLES.Tabasco_400,
            color: '#FFFFFF',
            fontWeight: '400',
        },
    ]),
    titleText: mbTextStyles([
        textStyles.largestText,
        {
            fontSize: 45,
            fontFamily: FONT_STYLES.Tabasco_400,
            color: '#FFFFFF',
            fontWeight: '400',
        },
    ]),
    content: {
        paddingHorizontal: 40,
    },
    greetingText: mbTextStyles([
        textStyles.normalText,
        {
            color: '#292E30',
        },
    ]),
    highlightCard: {
        marginVertical: 15,
        paddingVertical: 5,
    },
    highlightCardText: {
        fontSize: 55,
    },
    subGreetingText: mbTextStyles([
        textStyles.normalText,
        {
            color: '#292E30',
            marginBottom: 25,
        },
    ]),
    btn: {
        width: 227,
        alignSelf: 'center',
    },
});
